import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import PrintIcon from '@mui/icons-material/Print';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import {
  DATE_FORMAT_STRING,
  getDateOnly,
  formatDate,
  formatCurrency,
} from '#common/utils';
import {
  useGetSalespersonReportMutation,
} from '#services/order';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';

import SalespersonReportTable from './ReportTable';

interface Salesperson {
  id: string | number;
  username?: string;
  createdDate?: string;
  [key: string]: unknown;
}

interface SalespersonReportRow {
  productId?: string | number;
  style?: string;
  price?: number;
  isSampleItem?: boolean;
  currentCommissionFeePct?: number;
  commissionFee?: number;
  relatedOrders?: Array<string | number>;
  highlighted?: boolean;
  [key: string]: unknown;
}

interface SalespersonReportItem {
  rows: EnhancedTableRow[];
  customer: { username: string; code: string };
  subtotal: number;
}

interface ReportData {
  reports: SalespersonReportItem[];
  total: number;
  createdDate: Date | string;
  salespersons: Salesperson[];
  dateFrom?: number;
}

interface QueryOptions {
  dateFrom?: number;
  dateTo?: number;
  salespersonId?: string | number;
}

export default function SalespersonReport() {
  const navigate = useNavigate();

  const [getReport] = useGetSalespersonReportMutation();
  const [reportData, setReportData] = useState<ReportData>({
    reports: [],
    total: 0,
    createdDate: new Date(),
    salespersons: [],
  });

  const initialDateFrom = new Date(getDateOnly().setDate(1));
  const initialDateTo = getDateOnly();

  const [selectedSalesperson, setSelectedSalesperson] = useState(0);
  const [dateFrom, setDateFrom] = useState<Date>(initialDateFrom);
  const [dateTo, setDateTo] = useState<Date>(initialDateTo);

  const {
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const queryOptions: QueryOptions = {
  };

  if (dateFrom) {
    queryOptions.dateFrom = dateFrom.getTime();
  }
  if (dateTo) {
    queryOptions.dateTo = dateTo.getTime();
  }
  if (reportData.salespersons.length) {
    queryOptions.salespersonId = reportData.salespersons[selectedSalesperson]!.id;
  }

  let dateFromMinDate = new Date();
  if (reportData.salespersons[selectedSalesperson]) {
    dateFromMinDate = new Date(reportData.salespersons[selectedSalesperson].createdDate as string);
  }

  useEffect(() => {
    void getReport({
      ...queryOptions,
    }).then((res) => {
      const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
      if (hasError) {
        return;
      }

      const data = (res as { data: ReportData }).data;
      setReportData(data);

      const newDateFrom = new Date(data.dateFrom as number);
      if (dateFrom.getTime() !== newDateFrom.getTime()) {
        setDateFrom(newDateFrom);
      }
    });
  }, [dateFrom, dateTo, selectedSalesperson]);

  const handleReportExport = () => {
    const exportUrl = `/api/export/salespersonReport?dateFrom=${queryOptions.dateFrom}&dateTo=${queryOptions.dateTo}&salespersonId=${queryOptions.salespersonId}`;
    window.open(exportUrl, '_blank');
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Salesperson Report
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Box>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <Select
              value={selectedSalesperson}
              onChange={(e) => {
                setSelectedSalesperson(Number(e.target.value));
                setDateFrom(initialDateFrom);
                setDateTo(initialDateTo);
              }}
            >
              {
                reportData.salespersons.map(
                  (sp, idx) => <MenuItem key={sp.id} value={idx}>{sp.username}</MenuItem>,
                )
              }
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
          >
            From:&nbsp;
            <DesktopDatePicker
              format={DATE_FORMAT_STRING}
              name="dateRangeFrom"
              minDate={dateFromMinDate}
              value={dateFrom}
              maxDate={dateTo}
              slotProps={{ textField: { size: 'small' } }}
              onChange={(d) => {
                setDateFrom(d as Date);
                if (dateTo === undefined) {
                  setDateTo(new Date());
                }
              }}
            />
            &nbsp;To:&nbsp;
            <DesktopDatePicker
              format={DATE_FORMAT_STRING}
              name="dateRangeTo"
              value={dateTo}
              minDate={dateFrom}
              maxDate={new Date()}
              slotProps={{ textField: { size: 'small' } }}
              onChange={(d) => {
                setDateTo(d as Date);
                if (dateFrom === undefined) {
                  setDateFrom(new Date());
                }
              }}
            />

          </LocalizationProvider>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Box />

        <Box>
          <Button
            variant="text"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <ButtonGroup variant="outlined">
            <Button
              onClick={handleReportExport}
            >
              Export
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {
        reportData.reports.map((report, idx) => {
          const tableHeaders: EnhancedTableProps['headers'] = [{
            id: 'style',
            label: 'Style',
            renderer: (row) => {
              const reportRow = row as SalespersonReportRow;
              const url = `/shop/add?productId=${reportRow.productId}`;
              return (
                <Link
                  href={url}
                  onClick={(e) => {
                    e.preventDefault();
                    void navigate(url);
                  }}
                  sx={{ textDecoration: 'inherit' }}
                >
                  {reportRow.style}
                </Link>
              );
            },
          }, {
            id: 'variant',
            label: 'Variant',
          }, {
            id: 'qty',
            label: 'QTY',
          }, {
            id: 'price',
            label: 'Price',
            renderer: (row) => formatCurrency(
              (row as SalespersonReportRow).price as unknown as Parameters<typeof formatCurrency>[0],
            ),
          }, {
            id: 'commissionType',
            label: 'Commission Type',
            renderer: (row) => ((row as SalespersonReportRow).isSampleItem ? 'Sample Item' : 'Reorder'),
          }, {
            id: 'commissionPct',
            label: 'Commission %',
            renderer: (row) => `${(row as SalespersonReportRow).currentCommissionFeePct} %`,
          }, {
            id: 'commissionFee',
            label: 'Commission Fee',
            renderer: (row) => formatCurrency(
              (row as SalespersonReportRow).commissionFee as unknown as Parameters<typeof formatCurrency>[0],
            ),
          }, {
            id: 'relatedOrders',
            label: 'Orders',
            renderer: (row) => {
              const { relatedOrders } = row as SalespersonReportRow;
              return (relatedOrders || []).map((orderId) => {
                const url = `/management/order/detail?orderId=${orderId}`;
                return (
                  <Link
                    key={orderId}
                    href={url}
                    onClick={(e) => {
                      e.preventDefault();
                      void navigate(url);
                    }}
                    sx={{ textDecoration: 'inherit', mr: 1 }}
                  >
                    {orderId}
                  </Link>
                );
              });
            },
          }];

          const tableProps: Omit<EnhancedTableProps, 'rows'> = {
            name: 'salesperson-report-table',
            headers: tableHeaders,
            pinnedColumns: {
              left: ['style', 'variant'],
            },
            getRowStyle: (row) => {
              if ((row as SalespersonReportRow).highlighted) {
                return {
                  backgroundColor: 'rgba(236, 117, 93, 0.4)',
                  ':hover': { backgroundColor: 'rgba(221, 71, 57, 0.5)' },
                };
              }
              return {};
            },
          };

          return (
            <SalespersonReportTable
              key={idx}
              report={report}
              tableProps={tableProps}
            />
          );
        })
      }

      <Divider sx={{ my: 1 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Box>
          Report Date:&nbsp;
          {formatDate(reportData.createdDate)}
        </Box>
        <Box>
          <b>Total</b>
          :
          {' '}
          <span data-testid="subtotal">{formatCurrency(reportData.total)}</span>
        </Box>
      </Box>

      <Box
        sx={{
          color: 'darkred',
          typography: 'subtitle2',
          textAlign: 'right',
        }}
      >
        Please focus on the highlighted rows,
        <br />
        as they frequently signify potential issues with the commission calculation.
        <br />
        Due to ongoing order updates, this report reflects a snapshot of statuses within the selected date range.
        <br />
        Result may differ in future reports.
      </Box>
    </>
  );
}
